@if (inspection()) {
  <dbw-anchor-navigation
    ariaLabel="Navigation du viewer investisseur"
    [items]="investorViewerAnchors()"></dbw-anchor-navigation>
}

<section
  id="investor-viewer-fiche"
  class="page-shell">
  <header class="page-hero dbw-card dbw-card--xl">
    <div>
      <p class="eyebrow">Atlas</p>
      <h3>
        {{ inspection()?.investor?.displayName || 'Investisseur #' + investorId() }}
      </h3>
    </div>

    <div class="hero-actions">
      <oui-button
        color="secondary"
        type="outlined"
        iconSuffix="sync"
        (click)="reload()"
        [disabled]="loading()">
        Recharger
      </oui-button>
      @if (canExecutePurgeAction()) {
        <oui-button
          type="outlined"
          iconSuffix="delete"
          (click)="openPurgeDialog()"
          [disabled]="loading() || !inspection()">
          Purger
        </oui-button>
      }
    </div>
  </header>

  @if (purgeBannerExecution()) {
    <oui-banner
      type="complex"
      [declination]="purgeBannerDeclination()"
      [title]="purgeBannerTitle()"
      [description]="purgeBannerDescription()"
      [showCloseButton]="false"></oui-banner>
  }

  @if (loading()) {
    <dbw-state-card appearance="loading">
      <p>Chargement de l'inspection investisseur en cours.</p>
    </dbw-state-card>
  }

  @if (error() && !loading()) {
    <dbw-state-card
      appearance="error"
      title="Chargement impossible">
      <p>{{ error() }}</p>
    </dbw-state-card>
  }

  @if (inspection(); as vm) {
    <dbw-summary-grid [items]="summaryGridItems()"></dbw-summary-grid>

    <dbw-state-card
      appearance="info"
      title="Diagnostic global">
      <div class="diagnostic-summary">
        <p>{{ summaryMessage() }}</p>
        <div class="tag-cluster diagnostic-tags">
          @for (diagnostic of diagnostics(); track diagnostic.key) {
            <oui-tag
              type="status"
              size="big"
              [status]="diagnostic.status">
              {{ diagnostic.label }}
            </oui-tag>
          }
        </div>
      </div>
    </dbw-state-card>

    <section class="content-grid">
      <dbw-investor-viewer-accordion
        accordionTitle="Fiche investisseur"
        [expanded]="true">
        <div
          accordion-description
          class="tag-cluster">
          <oui-tag
            type="status"
            size="big"
            [status]="vm.hasDuplicatedArianeId ? 'error' : 'completed'">
            {{ vm.hasDuplicatedArianeId ? 'ArianeId en doublon' : 'ArianeId unique' }}
          </oui-tag>
          <oui-tag
            type="status"
            size="big"
            [status]="vm.hasPersonnePhysique ? 'completed' : 'error'">
            {{ vm.hasPersonnePhysique ? 'Personne physique liée' : 'Sans personne physique' }}
          </oui-tag>
          @if (vm.hasMultiplePersonnesPhysiques) {
            <oui-tag
              type="status"
              size="big"
              status="error"
              >Plusieurs personnes physiques</oui-tag
            >
          }
        </div>

        <div class="identity-grid">
          <article class="identity-item dbw-card dbw-card--lg">
            <span class="identity-item__label">Nom affiché</span><strong>{{ vm.investor.displayName }}</strong>
          </article>
          <article class="identity-item dbw-card dbw-card--lg">
            <span class="identity-item__label">Code investisseur</span><strong>{{ vm.investor.investorCode || '-' }}</strong>
          </article>
          <article class="identity-item dbw-card dbw-card--lg">
            <span class="identity-item__label">ArianeId</span><strong>{{ vm.investor.arianeId ?? '-' }}</strong>
          </article>
          <article class="identity-item dbw-card dbw-card--lg">
            <span class="identity-item__label">Statut</span
            ><dbw-investor-status-tag [statusCode]="vm.investor.statusCode"></dbw-investor-status-tag>
          </article>
          <article class="identity-item dbw-card dbw-card--lg">
            <span class="identity-item__label">Consultant principal</span
            ><dbw-consultant-card
              [id]="vm.investor.consultantId"
              [code]="vm.investor.consultantCode || '-'"
              [name]="vm.investor.consultantName || '-'"
              [statusCode]="vm.investor.consultantStatusCode"></dbw-consultant-card>
          </article>
          <article class="identity-item dbw-card dbw-card--lg">
            <span class="identity-item__label">Personnes physiques Atlas</span
            ><strong>{{ formatPersonnePhysiqueIds(vm.investor.personnePhysiqueIds) }}</strong>
          </article>
        </div>
      </dbw-investor-viewer-accordion>

      <dbw-investor-external-references-section
        [investorId]="investorId()"
        [boxPlacement]="boxPlacement()"
        [boxPlacementLoading]="boxPlacementLoading()"
        [boxPlacementError]="boxPlacementError()"
        [immoOptionIntention]="immoOptionIntention()"
        [immoLoading]="immoLoading()"
        [immoError]="immoError()"></dbw-investor-external-references-section>

      @if (vm.duplicatedArianeGroup; as duplicatedGroup) {
        <article class="panel dbw-card dbw-card--xl">
          <div class="panel-heading">
            <div>
              <h4>Groupe ArianeId</h4>
              <p>ArianeId {{ duplicatedGroup.arianeId }} partagé par {{ duplicatedGroup.duplicateCount }} investisseur(s)</p>
            </div>
            <oui-button
              type="outlined"
              color="secondary"
              (click)="openArianeDuplicate(duplicatedGroup.arianeId)">
              Inspecter le doublon Ariane
            </oui-button>
          </div>

          <div class="group-grid">
            <article class="group-item dbw-card dbw-card--lg">
              <span class="identity-item__label">Investisseurs</span>
              <div class="inline-actions">
                @for (linkedInvestorId of duplicatedGroup.investorIds; track linkedInvestorId) {
                  <oui-button
                    type="text"
                    color="secondary"
                    (click)="openInvestor(linkedInvestorId)">
                    #{{ linkedInvestorId }}
                  </oui-button>
                }
              </div>
            </article>
            <article class="group-item dbw-card dbw-card--lg">
              <span class="identity-item__label">Noms</span><strong>{{ duplicatedGroup.investorNames.join(' • ') || '-' }}</strong>
            </article>
            <article class="group-item dbw-card dbw-card--lg">
              <span class="identity-item__label">Statuts</span>
              <div class="status-tag-list">
                @for (statusCode of duplicatedGroup.statusCodes; track statusCode + $index) {
                  <dbw-investor-status-tag [statusCode]="statusCode"></dbw-investor-status-tag>
                } @empty {
                  <dbw-investor-status-tag [statusCode]="undefined"></dbw-investor-status-tag>
                }
              </div>
            </article>
            <article class="group-item dbw-card dbw-card--lg">
              <span class="identity-item__label">Consultants</span><strong>{{ duplicatedGroup.consultants.join(' • ') || '-' }}</strong>
            </article>
          </div>
        </article>
      }

      @if (viewerOverview(); as overview) {
        <dbw-investor-viewer-overview-section
          [viewer]="overview"
          [gerePar]="vm.investor.gerePar"></dbw-investor-viewer-overview-section>

        @if (viewerConsultants(); as assignments) {
          <dbw-investor-viewer-consultants-section [assignments]="assignments"></dbw-investor-viewer-consultants-section>
        } @else {
          @if (viewerConsultantsLoading()) {
            <dbw-state-card
              appearance="loading"
              title="Consultants Atlas">
              <p>Chargement des affectations consultants en cours.</p>
            </dbw-state-card>
          } @else if (viewerConsultantsError()) {
            <dbw-state-card
              appearance="error"
              title="Consultants Atlas">
              <p>{{ viewerConsultantsError() }}</p>
            </dbw-state-card>
          }
        }

        <dbw-investor-viewer-persons-section
          [investorId]="investorId()"
          [currentConsultantId]="vm.investor.consultantId"
          [summaries]="overview.persons"
          [persons]="viewerPersons() ?? []"
          [loading]="viewerPersonsLoading()"
          [error]="viewerPersonsError()"
          (correctionExecuted)="reload()"
          (relationDeleted)="reloadViewerPersons()"></dbw-investor-viewer-persons-section>

        @if (viewerFinancial(); as financialData) {
          <div class="viewer-financial-grid">
            <dbw-investor-viewer-objectives-section [objectives]="financialData.objectives"></dbw-investor-viewer-objectives-section>
            <dbw-investor-viewer-assets-section
              [assets]="financialData.assets"
              [liabilities]="financialData.liabilities"></dbw-investor-viewer-assets-section>
            <dbw-investor-viewer-liabilities-section [liabilities]="financialData.liabilities"></dbw-investor-viewer-liabilities-section>
            <dbw-investor-viewer-revenue-charges-section
              [items]="financialData.revenueAndCharges"></dbw-investor-viewer-revenue-charges-section>
            <dbw-investor-viewer-tax-section [taxes]="financialData.taxes"></dbw-investor-viewer-tax-section>
          </div>
        } @else {
          @if (viewerFinancialLoading()) {
            <dbw-state-card
              appearance="loading"
              title="Patrimoine et fiscalité">
              <p>Chargement des données financières Atlas en cours.</p>
            </dbw-state-card>
          } @else if (viewerFinancialError()) {
            <dbw-state-card
              appearance="error"
              title="Patrimoine et fiscalité">
              <p>{{ viewerFinancialError() }}</p>
            </dbw-state-card>
          }
        }
      } @else {
        @if (viewerOverviewLoading()) {
          <dbw-state-card
            appearance="loading"
            title="Viewer Atlas">
            <p>Chargement de la synthèse Atlas en cours.</p>
          </dbw-state-card>
        } @else if (viewerOverviewError()) {
          <dbw-state-card
            appearance="error"
            title="Viewer indisponible">
            <p>{{ viewerOverviewError() }}</p>
          </dbw-state-card>
        } @else {
          <dbw-state-card
            appearance="info"
            title="Viewer indisponible">
            <p>Les données détaillées du viewer historique n'ont pas pu être reconstituées pour cet investisseur.</p>
          </dbw-state-card>
        }
      }

      <dbw-investor-viewer-commands-section
        [commands]="viewerCommands()"
        [loading]="viewerCommandsLoading()"
        [error]="viewerCommandsError()"></dbw-investor-viewer-commands-section>

      <dbw-investor-ged-section
        [documents]="gedDocuments()"
        [loading]="gedLoading()"
        [error]="gedError()"></dbw-investor-ged-section>

      <dbw-investor-minotaure-section
        [investorId]="investorId()"
        [corrections]="vm.corrections"
        [anomalies]="vm.anomalies"
        [metadatas]="vm.metadatas"
        [loading]="minotaureLoading()"
        [error]="minotaureError()"></dbw-investor-minotaure-section>
    </section>
  }
</section>
